import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { phongBanRepository } from '../../../lib/repositories/phong-ban.repository';
import { useToast } from '../../../lib/toast';
import type { Department, DepartmentCoPhienBan, DepartmentCreate, DepartmentUpdate } from '../types';

/**
 * Khoá cache. Cây và từng phòng ban (kèm `ETag`) chung gốc `departments`, nên
 * vô hiệu hoá một lần là cả hai cùng đọc lại.
 */
export const KHOA_PHONG_BAN = {
  goc: ['departments'] as const,
  cay: ['departments', 'cay'] as const,
  chiTiet: (id: string) => ['departments', 'chi-tiet', id] as const,
};

/** 4xx là câu trả lời của server, không phải trục trặc: thử lại chỉ bắt người dùng chờ thêm. */
function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/** Cỡ trang lớn nhất API nhận (mục 6.1). */
const MOT_TRANG = 100;

/** Trần số trang khi nối: chặn vòng lặp nếu server trả `next_cursor` lặp lại. */
const TRANG_TOI_DA = 50;

/**
 * Cả cây phòng ban, phẳng — dùng chung cho màn Cơ cấu tổ chức và ô chọn phòng
 * ban ở màn người dùng. Nối `next_cursor` tới hết: tổ chức có vài chục tới vài
 * trăm phòng ban, một vài trang là đủ. `bat = false` khi nơi dùng chưa cần.
 */
export function useCayPhongBan(bat = true) {
  return useQuery<Department[], ApiError>({
    queryKey: KHOA_PHONG_BAN.cay,
    queryFn: async () => {
      const ra: Department[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < TRANG_TOI_DA; i += 1) {
        const trang = await phongBanRepository.danhSach({ limit: MOT_TRANG, cursor });
        ra.push(...trang.items);
        cursor = trang.next_cursor ?? undefined;
        if (!cursor) break;
      }
      return ra;
    },
    staleTime: 30_000,
    retry: thuLai,
    enabled: bat,
  });
}

/**
 * Một phòng ban kèm `ETag`, cho hộp sửa. Luôn đọc mới khi mở hộp
 * (`staleTime: 0`): ETag cũ trong cache chỉ dẫn tới `412`.
 */
export function useChiTietPhongBan(id: string | null) {
  return useQuery<DepartmentCoPhienBan, ApiError>({
    queryKey: KHOA_PHONG_BAN.chiTiet(id ?? ''),
    queryFn: () => phongBanRepository.chiTiet(id!),
    enabled: id !== null,
    staleTime: 0,
    gcTime: 0,
    retry: thuLai,
  });
}

/**
 * Sau mọi lần ghi: số người, số phòng con của **cha cũ và cha mới** đều đổi,
 * nên đọc lại cả cây thay vì vá từng dòng.
 */
function useDocLaiCay() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KHOA_PHONG_BAN.goc });
}

/**
 * Tạo phòng ban. `Idempotency-Key` mới cho **mỗi lần bấm**, như tạo vai trò.
 * Lỗi không bật toast: form hiện lỗi ngay trong hộp.
 */
export function useTaoPhongBan() {
  const toast = useToast();
  const { t } = useT();
  const docLai = useDocLaiCay();
  return useMutation<DepartmentCoPhienBan, ApiError, DepartmentCreate>({
    mutationFn: (du) => phongBanRepository.tao(du, crypto.randomUUID()),
    onSuccess: (moi) => {
      void docLai();
      toast.success(t('coCauToChuc.daTao', { ten: moi.name }));
    },
  });
}

/**
 * Đổi tên hoặc đổi cha, kèm `If-Match`. `412` (người khác vừa sửa) và `404`
 * (vừa bị xoá) thì đọc lại để hộp và cây nói đúng hiện trạng.
 */
export function useSuaPhongBan() {
  const toast = useToast();
  const { t } = useT();
  const docLai = useDocLaiCay();
  return useMutation<DepartmentCoPhienBan, ApiError, { id: string; du: DepartmentUpdate; etag: string }>({
    mutationFn: ({ id, du, etag }) => phongBanRepository.sua(id, du, etag),
    onSuccess: (moi) => {
      void docLai();
      toast.success(t('coCauToChuc.daLuu', { ten: moi.name }));
    },
    onError: (err) => {
      if (err.status === 412 || err.status === 404) void docLai();
    },
  });
}

/** Xoá phòng ban. `409` (vừa có người được gán vào) thì đọc lại cây để số người đúng. */
export function useXoaPhongBan() {
  const toast = useToast();
  const { t } = useT();
  const docLai = useDocLaiCay();
  return useMutation<void, ApiError, Department>({
    mutationFn: (d) => phongBanRepository.xoa(d.id),
    onSuccess: (_kq, d) => {
      void docLai();
      toast.success(t('coCauToChuc.daXoa', { ten: d.name }));
    },
    onError: (err) => {
      if (err.status === 409 || err.status === 404) void docLai();
    },
  });
}
